import { ChangeEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useSuggestionSearchInputState from 'components/desktop/_general/Input/SuggestionSearchInput/useSuggestionSearchInputState';
import * as SelectItem from 'types/_general/SelectItem';
import * as SelectItemList from 'types/_general/SelectItemList';

interface IProps {
	allItems: SelectItemList.Type;
	disabled?: boolean;
	errorKey?: string;
	savedItems: string[];
	setSavedItem: (items: string) => void;
}

const SuggestionSearchInputNew = (props: IProps) => {
	const { t } = useTranslation();
	const { state, handlers } = useSuggestionSearchInputState();
	const inputElement = useRef<HTMLInputElement | null>(null);
	const currentListItem = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (currentListItem.current)
			currentListItem.current.scrollIntoView({
				behavior: 'auto',
				block: 'nearest',
				inline: 'nearest',
			});
	}, [state.selectionIndex]);

	const handleSelectItem = (item: SelectItem.Type): void => {
		props.setSavedItem(props.allItems.filter((e) => e.id === item.id).map((x) => x.id)[0]);
		handlers.setSearchTerm('');
		inputElement.current?.blur();
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
		const selectedItem = props.allItems[state.selectionIndex];
		switch (e.key) {
			case 'ArrowUp':
				if (state.selectionIndex > 0) handlers.setSelectionIndex(state.selectionIndex - 1);
				e.preventDefault();
				break;
			case 'ArrowDown':
				if (state.selectionIndex < props.allItems.length - 1)
					handlers.setSelectionIndex(state.selectionIndex + 1);
				e.preventDefault();
				break;
			case 'Enter':
				if (selectedItem) handleSelectItem(selectedItem);
				break;
			case 'Escape':
				inputElement.current?.blur();
				break;
		}
	};

	const onMouseDown = (item: SelectItem.Type): void => {
		handleSelectItem(item);
	};

	return (
		<>
			<input
				className="form-control"
				type="text"
				value={state.searchTerm}
				onChange={(e: ChangeEvent<HTMLInputElement>) =>
					handlers.onChange(e.currentTarget.value)
				}
				onBlur={handlers.onBlur}
				onFocus={handlers.onFocus}
				onKeyDown={onKeyDown}
				error-key={props.errorKey}
				disabled={props.disabled}
				placeholder={
					props.disabled
						? t('_general:INPUT_PLACEHOLDER_NO_MORE_ENTRIES')
						: t('_general:INPUT_PLACEHOLDER_SEARCHTERM')
				}
			/>
			{state.showSuggested && props.allItems.length > 0 && (
				<div className="select-suggestions">
					{SelectItemList.filterSuggested(
						props.allItems,
						state.searchTerm,
						props.savedItems
					).map((item: SelectItem.Type, index: number) => (
						<div
							className={index === state.selectionIndex ? 'active' : ''}
							ref={index === state.selectionIndex ? currentListItem : undefined}
							key={index}
							onMouseDown={() => onMouseDown(item)}
						>
							{item.name}
						</div>
					))}
				</div>
			)}
		</>
	);
};

export default SuggestionSearchInputNew;
